<template>
  <div>
    <div class="box">
      <loading v-if="isLoading" />

      <div v-if="!isLoading">
        <div class="columns">
          <div class="column">
            <h2 class="title has-text-primary">
              <fa-icon class="margin-right-2" :icon="faChartNetwork" />Activity Groups
            </h2>
          </div>
          <div class="column is-narrow">
            <div class="buttons">
              <button class="button is-primary is-outlined margin-right-2" @click="createActivityGroup">
                <fa-icon :icon="faPlus" />Add
              </button>
              <custom-button modifier="primary" :icon="faSyncAlt" iconPosition="left" @click="refresh">Refresh</custom-button>
            </div>
          </div>
        </div>

        <div class="panel">
          <div class="panel-heading">
            <div class="columns">
              <div class="column">
                <div class="margin-top-1">Filter Options</div>
              </div>
              <div class="column margin-top-1 is-narrow">{{filteredActivityGroups.length}} result(s)</div>

              <div class="column is-narrow">
                <button class="button is-primary is-outlined" @click="resetFilters">
                  <fa-icon :icon="faUndo" />Reset Filters
                </button>
              </div>
            </div>
          </div>
          <div class="panel-block">
            <p class="control has-icons-left">
              <input class="input" v-model="groupSearchTerm" placeholder="Search..." />
              <span class="icon is-left">
                <fa-icon :icon="faSearch" />
              </span>
            </p>
          </div>
        </div>

        <table class="table is-striped is-hoverable is-fullwidth" v-if="filteredActivityGroups.length > 0">
          <thead>
            <tr>
              <th>Name</th>
              <th>Order</th>
              <th class="is-narrow"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(activityGroup, index) in filteredActivityGroups" :key="index">
              <td>{{activityGroup.title}}</td>
              <td>{{activityGroup.order}}</td>
              <td class="is-narrow">
                <div class="buttons is-pulled-right is-inline-block">
                  <custom-button class="is-inline-block margin-right-2" modifier="primary" :icon="faEdit" iconPosition="left" @click="editActivityGroup(activityGroup)">Edit</custom-button>
                  <custom-button class="is-inline-block margin-right-2" modifier="primary" :icon="faCogs" iconPosition="left" @click="$router.push({path: '/manage/activity-groups/' + activityGroup.id});">Activites</custom-button>
                  <custom-button class="is-inline-block" modifier="danger" :icon="faTrashAlt" iconPosition="left" @click="deleteActivityGroup(activityGroup)">Delete</custom-button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
        <div class="has-text-centered" v-if="activityGroups.length === 0">No activity groups found.</div>
        <div class="has-text-centered" v-if="activityGroups.length > 0 && filteredActivityGroups.length === 0">No matching activity groups found.</div>
      </div>
    </div>

    <modal v-if="showEditActivityGroupModal" title="Create Activity Group" @close="cancelEditingActivityGroup" @submit="saveActivityGroupBeingEdited">
      <div class="field is-horizontal">
        <div class="field-label is-normal">
          <label class="label">Title</label>
        </div>
        <div class="field-body">
          <div class="field">
            <div class="control">
              <input class="input" :class="{'is-danger' : $v.activityGroupBeingEdited.$dirty && $v.activityGroupBeingEdited.title.$anyError}" type="text" v-model="$v.activityGroupBeingEdited.title.$model" />
              <div class="help has-text-weight-semibold has-text-danger" v-if="$v.activityGroupBeingEdited.$dirty && !$v.activityGroupBeingEdited.title.required">{{$v.activityGroupBeingEdited.title.$params.required.msg}}</div>
              <div class="help has-text-weight-semibold has-text-danger" v-if="$v.activityGroupBeingEdited.$dirty && !$v.activityGroupBeingEdited.title.maxLength">{{$v.activityGroupBeingEdited.title.$params.maxLength.msg}}</div>
            </div>
          </div>
        </div>
      </div>

      <div class="field is-horizontal">
        <div class="field-label is-normal">
          <label class="label">Order</label>
        </div>
        <div class="field-body">
          <div class="field">
            <div class="control">
              <input class="input" :class="{'is-danger' : $v.activityGroupBeingEdited.$dirty && $v.activityGroupBeingEdited.order.$anyError}" type="number" min="1" v-model="$v.activityGroupBeingEdited.order.$model" />
              <div class="help has-text-weight-semibold has-text-danger" v-if="$v.activityGroupBeingEdited.$dirty && !$v.activityGroupBeingEdited.order.required">{{$v.activityGroupBeingEdited.order.$params.required.msg}}</div>
              <div class="help has-text-weight-semibold has-text-danger" v-if="$v.activityGroupBeingEdited.$dirty && !$v.activityGroupBeingEdited.order.minValue">{{$v.activityGroupBeingEdited.order.$params.minValue.msg}}</div>
            </div>
          </div>
        </div>
      </div>

      <div class="field is-horizontal">
        <div class="field-label is-normal"></div>
        <div class="field-body">
          <div class="field">
            <div class="control">
              <label class="checkbox">
                <input type="checkbox" v-model="activityGroupBeingEdited.isTimeOff" /> Activities in this group count towards time off
              </label>
            </div>
          </div>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import loading from "@/components/loading"
import customButton from "@/components/custom-button"
import modal from "@/components/modal"
import { faChartNetwork } from "@fortawesome/pro-solid-svg-icons"
import { faSyncAlt, faSearch, faUndo, faEdit, faTrashAlt, faPlus, faChevronLeft, faCogs } from "@fortawesome/free-solid-svg-icons"
import { withParams } from 'vuelidate';
import { required, maxLength, minValue } from "vuelidate/lib/validators";
import {shallowCopy} from "@/utilities";

export default {
  components: {
    loading,
    customButton,
    modal
  },
  computed: {
    filteredActivityGroups() {
      if (!this.groupSearchTerm || this.groupSearchTerm === "")
        return this.activityGroups

      return this.activityGroups.filter(x => x.title.toLowerCase().includes(this.groupSearchTerm.toLowerCase()))
    }
  },
  data() {
    return {
      isLoading: true,
      activityGroups: [],
      groupSearchTerm: "",
      activityGroupBeingEdited: {
        id: null,
        title: null,
        order: null,
        isTimeOff: false
      },
      showEditActivityGroupModal: false,
      faChartNetwork,
      faSyncAlt,
      faSearch,
      faUndo,
      faEdit,
      faTrashAlt,
      faPlus,
      faChevronLeft,
      faCogs
    }
  },
  methods: {
    refresh() {
      this.isLoading = true

      this.$http.get("ActivityGroups").then(response => {
        this.activityGroups = response.data
        this.isLoading = false
      }).catch(() => {
        this.isLoading = false
        this.$alert("An error occurred loading the activity groups.", "", "error");
      })
    },
    deleteActivityGroup(activityGroup) {
      this.$confirm("Are you sure you wish to delete the activity group '" + activityGroup.title + "'? All activities within the group will also be deleted.", "Delete activity group?", "question").then(() => {
        this.$http.delete("ActivityGroups/" + activityGroup.id).then(() => {
          this.refresh();
          this.$alert("Activity group deleted successfully.", "", "success");
        }).catch((error) => {
          if (error.response.data.message) {
            this.$alert(error.response.data.message, "", "error");
          } else this.$alert("An error occurred deleting the activity group.", "", "error");
        });
      });
    },
    resetFilters() {
      this.groupSearchTerm = "";
    },
    createActivityGroup() {
      this.$v.activityGroupBeingEdited.$reset();

      let nextOrder = Math.max.apply(Math, this.activityGroups.map((o) => o.order));

      if (nextOrder < 0)
        nextOrder = 0;
      else nextOrder = nextOrder + 1;

      this.activityGroupBeingEdited = {
        id: null,
        title: null,
        order: nextOrder,
        isTimeOff: false
      };

      this.showEditActivityGroupModal = true;
    },
    editActivityGroup(activityGroup) {
      this.$v.activityGroupBeingEdited.$reset();

      this.activityGroupBeingEdited = shallowCopy(activityGroup);
      
      this.showEditActivityGroupModal = true;
    },
    saveActivityGroupBeingEdited() {
      this.$v.activityGroupBeingEdited.$touch();

      if (this.$v.activityGroupBeingEdited.$invalid)
        return;

      this.isLoading = true

      let data = {
        title: this.activityGroupBeingEdited.title,
        order: parseInt(this.activityGroupBeingEdited.order),
        isTimeOff: this.activityGroupBeingEdited.isTimeOff
      };
      
      if (this.activityGroupBeingEdited.id != null) {
        data.id = this.activityGroupBeingEdited.id;

        this.$http.post("ActivityGroups", data).then(() => {
          this.showEditActivityGroupModal = false;
          this.refresh();
          this.$alert("Activity group updated successfully.", "", "success");
        }).catch((error) => {
          if (error.response.data.message) {
            this.$alert(error.response.data.message, "", "error");
          } else this.$alert("An error occurred updating the activity group.", "", "error");

          this.isLoading = false
        });
      }
      else {
        this.$http.put("ActivityGroups", data).then(() => {
          this.showEditActivityGroupModal = false;
          this.refresh();
          this.$alert("Activity group created successfully.", "", "success");
        }).catch((error) => {
          if (error.response.data.message) {
            this.$alert(error.response.data.message, "", "error");
          } else this.$alert("An error occurred creating the activity group.", "", "error");

          this.isLoading = false
        }); 
      }
    },
    cancelEditingActivityGroup() {
      this.showEditActivityGroupModal = false;
    }
  },
  created() {
    this.refresh()
  },
  validations: {
    activityGroupBeingEdited: {
      title: {
        required: withParams({ msg: "Please enter a title." }, required),
        maxLength: withParams({ msg: "The title must be no longer than 50 characters." }, maxLength(50))
      },
      order: {
        required: withParams({ msg: "Please enter a value for order." }, required),
        minValue: withParams({ msg: "The value for order must be greater than or equal to 1." }, minValue(1))
      }
    }
  }
}
</script>

<style lang="sass" scoped>
</style>